import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { usePoll } from "@/components/common";
import { UserPlus, Trash2, X, Loader2 } from "lucide-react";

export default function AdminOfficers() {
  const [officers, setOfficers] = useState([]);
  const [allPerms, setAllPerms] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", permissions: [] });
  const [busy, setBusy] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [delForm, setDelForm] = useState({ reason: "", password: "" });

  const load = () => api.get("/admin/officers").then((r) => setOfficers(r.data)).catch(() => {});
  useEffect(() => { api.get("/admin/permissions").then((r) => setAllPerms(r.data.permissions)).catch(() => {}); }, []);
  usePoll(load, 10000, []);

  const toggle = (list, perm, setter, obj) => {
    const next = list.includes(perm) ? list.filter((p) => p !== perm) : [...list, perm];
    setter(obj ? { ...obj, permissions: next } : next);
  };

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/admin/officers", form); toast.success("Officer created"); setShowNew(false); setForm({ name: "", email: "", password: "", permissions: [] }); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const updatePerms = async (officer, perms) => {
    try { await api.put(`/admin/officers/${officer.id}/permissions`, { permissions: perms }); toast.success("Permissions updated"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const confirmDelete = async () => {
    if (!delForm.reason.trim()) return toast.error("Reason is required");
    if (!delForm.password) return toast.error("Re-enter your password to confirm");
    setBusy(true);
    try {
      await api.delete(`/admin/officers/${delTarget.id}`, { data: delForm });
      toast.success("Officer deleted and access revoked");
      setDelTarget(null); setDelForm({ reason: "", password: "" }); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Officers</h1>
        <button onClick={() => setShowNew(!showNew)} data-testid="new-officer-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
          <UserPlus className="w-4 h-4" /> New officer
        </button>
      </div>

      {showNew && (
        <form onSubmit={create} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
          <div className="grid sm:grid-cols-3 gap-3">
            <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="officer-name" className="px-3 py-2 rounded-xl border border-slate-300 outline-none text-sm" />
            <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="officer-email" className="px-3 py-2 rounded-xl border border-slate-300 outline-none text-sm" />
            <input required type="password" placeholder="Password (min 8)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="officer-password" className="px-3 py-2 rounded-xl border border-slate-300 outline-none text-sm" />
          </div>
          <div className="flex flex-wrap gap-2">
            {allPerms.map((p) => (
              <button type="button" key={p} onClick={() => toggle(form.permissions, p, setForm, form)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium ${form.permissions.includes(p) ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"}`} data-testid={`officer-perm-${p}`}>{p}</button>
            ))}
          </div>
          <button disabled={busy} data-testid="create-officer-button" className="px-5 py-2.5 rounded-xl bg-blue-600 text-white font-semibold disabled:opacity-60">Create officer</button>
        </form>
      )}

      <div className="space-y-3">
        {officers.map((o) => (
          <div key={o.id} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`officer-row-${o.email}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold text-slate-900">{o.name}</div>
                <div className="text-sm text-slate-500 mb-3">{o.email}</div>
              </div>
              <button onClick={() => setDelTarget(o)} data-testid={`delete-officer-${o.email}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-rose-600 border border-rose-200 hover:bg-rose-50">
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {allPerms.map((p) => (
                <button key={p} onClick={() => updatePerms(o, o.permissions.includes(p) ? o.permissions.filter((x) => x !== p) : [...o.permissions, p])}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium ${o.permissions.includes(p) ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500"}`} data-testid={`officer-${o.email}-perm-${p}`}>{p}</button>
              ))}
            </div>
          </div>
        ))}
        {officers.length === 0 && <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-400">No officers yet.</div>}
      </div>

      {delTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => !busy && setDelTarget(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="delete-officer-modal">
            <div className="flex items-start justify-between">
              <h3 className="font-heading font-bold text-lg text-slate-900">Delete officer</h3>
              <button onClick={() => setDelTarget(null)} disabled={busy} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            <div className="text-sm text-slate-600">
              You are deleting <span className="font-semibold">{delTarget.name}</span> ({delTarget.email}). Their access is revoked immediately and active sessions are invalidated. Historical attribution and audit logs are preserved.
            </div>
            <input value={delForm.reason} onChange={(e) => setDelForm({ ...delForm, reason: e.target.value })} placeholder="Reason (required)"
              data-testid="delete-officer-reason" className="w-full px-3 py-2 rounded-xl border border-slate-300 outline-none text-sm" />
            <input type="password" value={delForm.password} onChange={(e) => setDelForm({ ...delForm, password: e.target.value })} placeholder="Re-enter your password"
              data-testid="delete-officer-password" className="w-full px-3 py-2 rounded-xl border border-slate-300 outline-none text-sm" />
            <button onClick={confirmDelete} disabled={busy} data-testid="delete-officer-confirm-button"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-rose-600 text-white font-semibold hover:bg-rose-700 disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Delete officer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
